import React, { useRef, useEffect } from "react";
import { Send, ArrowLeftRight } from "lucide-react";
import { Button } from "../ui/button";

export const MessageInput = ({
  newMessage,
  onMessageChange,
  onSendMessage,
  disabled = false,
  onCreateExchange,
  showExchangeButton = false,
}) => {
  const textareaRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
    }
  }, [newMessage]);

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (e.shiftKey) {
        return;
      } else {
        e.preventDefault();
        if (newMessage.trim() && !disabled) {
          onSendMessage();
        }
      }
    }
  };

  // Handle send button click
  const handleSend = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (newMessage.trim() && !disabled) {
      onSendMessage();
    }
  };

  // Handle exchange button click
  const handleExchange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onCreateExchange && !disabled) {
      onCreateExchange();
    }
  };

  // Simplified focus handler for iOS
  const handleFocus = (e) => {
    // Minimal iOS handling - let the browser handle most of it
    if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
      // Small delay to let iOS keyboard settle
      setTimeout(() => {
        if (e.target && e.target.scrollIntoView) {
          e.target.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 100);
    }
  };

  return (
    <div className="p-3 md:p-4 bg-white">
      <div className="flex items-center space-x-2 md:space-x-3">
        {/* Message Input Container - REMOVED onClick handler */}
        <div className="flex-1 relative flex items-center">
          <textarea
            ref={textareaRef}
            placeholder="Type your message..."
            className={`w-full px-3 py-2 md:px-4 md:py-3 border border-gray-200 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-igbanwo-mint focus:border-transparent transition-colors duration-200 placeholder-gray-500 text-sm leading-5 min-h-[40px] md:min-h-[48px] max-h-[100px] md:max-h-[120px] ${
              showExchangeButton ? "pr-12 md:pr-16" : "pr-10 md:pr-12"
            }`}
            value={newMessage}
            onChange={(e) => onMessageChange(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={handleFocus}
            disabled={disabled}
            rows={1}
            style={{
              fontSize: "16px", // Fixed 16px to prevent iOS zoom
              WebkitAppearance: "none",
              WebkitTapHighlightColor: "transparent",
              WebkitUserSelect: "text", // Ensure text selection works
              touchAction: "manipulation", // Better touch handling
            }}
            // Add these iOS-specific attributes
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="sentences"
            spellCheck="true"
          />

          {showExchangeButton && (
            <button
              onClick={handleExchange}
              disabled={disabled}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 md:p-2 text-igbanwo-mint hover:bg-igbanwo-mint/10 disabled:text-gray-400 disabled:hover:bg-transparent rounded-lg transition-colors duration-200"
              title="Create exchange"
              style={{
                WebkitTapHighlightColor: "transparent",
                touchAction: "manipulation",
              }}
            >
              <ArrowLeftRight className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          )}
        </div>

        {/* Send Button */}
        <Button
          onClick={handleSend}
          disabled={!newMessage.trim() || disabled}
          className="bg-igbanwo-mint hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 rounded-xl flex-shrink-0 flex items-center justify-center"
          style={{
            WebkitTapHighlightColor: "transparent",
            touchAction: "manipulation",
          }}
        >
          <Send className="w-4 h-4 md:w-5 md:h-5 text-white" />
        </Button>
      </div>

      {/* Helper Text - Hidden on mobile */}
      <div className="mt-2 text-xs text-gray-400 px-1 hidden md:block">
        Press Enter to send, Shift+Enter for new line
        {showExchangeButton && " • Click package icon to create exchange"}
      </div>
    </div>
  );
};